// patch 26: one title + description per page, so Google shows "Loja X — Porto | tierTCG" instead of the site name
// patch 42: canonical + og:url (without ?filters), html lang, optional noindex and JSON-LD
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useI18n } from "@/lib/i18n";

export const ORIGIN = "https://tiertcg.com";

// index.html carries default tags for crawlers that don't run JavaScript. Once a page sets its own, the defaults
// go, so Google never sees two descriptions (Helmet on React 19 renders tags itself and leaves static ones).
export function useDropStaticHead(lang) {
  useEffect(() => {
    document.querySelectorAll("head [data-static]").forEach((el) => el.remove());
    if (lang) document.documentElement.lang = lang === "en" ? "en" : "pt-PT";
  }, [lang]);
}

export default function Seo({ title, description, noindex = false, jsonLd = null, path = null }) {
  const { lang } = useI18n();
  const { pathname } = useLocation();
  const clean = (title || "").replace(/\s*[·|]\s*tierTCG\s*$/i, "");
  const full = clean ? `${clean} | tierTCG` : "tierTCG — rankings TCG de Portugal";
  const url = ORIGIN + (path ?? (pathname.replace(/\/+$/, "") || "/"));
  useDropStaticHead(lang);
  const blocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  return (
    <Helmet htmlAttributes={{ lang: lang === "en" ? "en" : "pt-PT" }}>
      <title>{full}</title>
      {description && <meta name="description" content={description} />}
      {noindex ? <meta name="robots" content="noindex, follow" /> : <link rel="canonical" href={url} />}
      <meta property="og:title" content={full} />
      {description && <meta property="og:description" content={description} />}
      {!noindex && <meta property="og:url" content={url} />}
      <meta name="twitter:title" content={full} />
      {description && <meta name="twitter:description" content={description} />}
      {blocks.map((b, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify({ "@context": "https://schema.org", ...b })}</script>
      ))}
    </Helmet>
  );
}
